import { IQuestion, ISurvey, ISurveyData, ISurveyImpl, ITextProcessor } from "./base-interfaces";
import { IObjectValueContext, IValueGetterContext, IValueGetterContextGetValueParams, IValueGetterInfo, VariableGetterContext } from "./conditions/conditionProcessValue";
import { Helpers } from "./helpers";
import { Question, QuestionItemValueGetterContext } from "./question";
import { settings } from "./settings";
import { TextContextProcessor } from "./textPreProcessor";

export interface IDynamicItemModelData {
    getSurvey(): ISurvey;
    getItem(index: number): DynamicItemModelBase;
    getItemData(item: ISurveyData): any;
    getItemIndex(item: ISurveyData): number;
    getValueGetterContext(): IValueGetterContext;
    getFilteredData(): any;
    getBindedQuestions(): IQuestion[];
    getSharedQuestionFromArray(name: string, index: number): Question;
    updateItemValue(item: ISurveyData, name: string, val: any, isDeletingValue: boolean): any;
}

export abstract class DynamicItemGetterContext extends QuestionItemValueGetterContext {
  constructor(protected item: DynamicItemModelBase) {
    super();
  }
  protected getIndex(): number { return this.item.getIndex(); }
  protected getQuestionData(): Question { return <Question>(<any>this.item.data); }
  protected get questionName(): string {
    return "";
  }
  protected abstract getSpecificValue(params: IValueGetterContextGetValueParams): IValueGetterInfo;
  protected abstract getNextName(): string;
  protected abstract getPrevName(): string;
  protected get variableName(): string {
    return this.item.getVariableName();
  }
  protected abstract getItemValue(name: string): any;
  public getValue(params: IValueGetterContextGetValueParams): IValueGetterInfo {
    const path = params.path;
    if (path.length === 0) return undefined;

    if (path.length === 1) {
      const val = this.getItemValue(path[0].name);
      if (val !== undefined) {
        return { isFound: true, value: val, context: this };
      }

      if (this.questionName && path[0].name === this.questionName) {
        const matrix = this.item.data;
        return { isFound: true, context: matrix.getValueGetterContext(), value: matrix.getFilteredData() };
      }
    }

    if (path.length > 1) {
      const dIndex = path[0].name === this.getPrevName() ? -1 : path[0].name === this.getNextName() ? 1 : 0;
      if (dIndex !== 0) {
        const index = this.visibleIndex + dIndex;
        const item = this.getVisibleItem(index);
        if (!item) return { isFound: true, value: undefined, context: this };
        path[0].name = this.variableName;
        return item.getValueGetterContext().getValue(params);
      }
    }

    const res = this.getSpecificValue(params);
    if (res) return res;

    const isVarPrefix = path[0].name === this.variableName;
    if (isVarPrefix || !params.isRoot) {
      if (isVarPrefix) {
        path.shift();
      }
      let res = super.getValue(params);
      if (!!res && res.isFound) return res;
      const allValues = this.item.getAllValues();
      if (params.isRoot) {
        res = this.getValueFromBindedQuestions(path, allValues);
        if (!!res) return res;
      }
      return new VariableGetterContext(allValues).getValue(params);
    }
    return undefined;
  }
  protected updateValueByItem(name: string, res: IValueGetterInfo): void {
    const qs = this.item.getQuestionsByValueName(name, true);
    if (qs.length > 0) {
      res.isFound = true;
      res.obj = qs[0];
      res.context = qs[0].getValueGetterContext();
    }
  }
  protected abstract getVisibleItem(idx: number): DynamicItemModelBase;
  protected abstract get visibleIndex(): number;
  protected getItemVariableNames(): Array<string> {
    return [];
  }
  public getContextKeys(): { [key: string]: any } {
    const res: { [key: string]: any } = {};
    const names = [this.variableName, this.questionName, this.getPrevName(), this.getNextName()]
      .concat(this.getItemVariableNames());
    names.forEach((name) => {
      if (name) {
        res[name] = this.item;
      }
    });
    return res;
  }
}

export abstract class DynamicItemModelBase implements ISurveyData, ISurveyImpl, IObjectValueContext {

  protected isSettingValue: boolean = false;
  private textPreProcessor: TextContextProcessor;
  constructor(public data: IDynamicItemModelData) {
    this.textPreProcessor = new TextContextProcessor(this);
  }
  abstract getValueGetterContext(): IValueGetterContext;
  getSurveyData(): ISurveyData {
    return this;
  }
  getTextProcessor(): ITextProcessor {
    return this.textPreProcessor;
  }

  public abstract getQuestionsByValueName(name: string, caseInsensitive?: boolean): Array<Question>;
  public abstract getVariableName(): string;
  protected abstract getQuestionByName(name: string): IQuestion;

  public abstract getIndex(): number;

  getSurvey(): ISurvey {
    return this.data ? this.data.getSurvey() : null;
  }
  getValue(name: string): any {
    return this.getAllValues()[name];
  }
  abstract setValue(name: string, newValue: any): void;

  getAllValues(): any {
    return this.data.getItemData(this);
  }

  abstract getComment(name: string): string;
  abstract setComment(name: string, newValue: string, locNotification: boolean): void;

  getFilteredProperties(): any {
    return { survey: this.getSurvey(), [this.getVariableName()]: this };
  }
  findQuestionByName(name: string): IQuestion {

    if (!name) return undefined;
    const prefix = this.getVariableName() + ".";
    if (name.indexOf(prefix) === 0) {
      return this.getQuestionByName(name.substring(prefix.length));
    }
    const survey = this.getSurvey();
    return !!survey ? survey.getQuestionByName(name) : null;
  }

  public abstract get questions(): Array<Question>;
  public runTriggers(name: string, value: any, keys?: any): void {
    if (!name && !keys) return;
    this.questions.forEach(q => q.runTriggers(name, value, keys));
  }

  protected runTriggersOnSetValue(name: string, newValue: any): void {
    const questions = this.questions;
    const suffix = settings.commentSuffix;
    if (name.endsWith(suffix)) {
      name = name.substring(0, name.length - suffix.length);
      const cQ = this.getQuestionByName(name);
      if (!!cQ) {
        newValue = cQ.value;
      }
    }
    const triggerName = this.getVariableName() + "." + name;
    for (var i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (q.getValueName() !== name) {
        q.checkBindings(name, newValue);
      }
      q.runTriggers(triggerName, newValue);
    }
    var index = this.data.getItemIndex(this);
    if (index < 0) return;
    const bindedQuestions = this.data.getBindedQuestions();
    bindedQuestions.forEach((q: any) => {
      if (q === this.data) return;
      if (typeof q.getItem === "function") {
        const item = q.getItem(index);
        if (item && item instanceof DynamicItemModelBase) {
          const triggerName = item.getVariableName() + "." + name;
          item.runTriggers(triggerName, newValue);
        }
      }
    });
  }

  protected updateSharedQuestionsValue(name: string, value: any): void {
    const questions = this.getQuestionsByValueName(name);
    if (questions.length > 1) {
      for (let i = 0; i < questions.length; i ++) {
        if (!Helpers.isTwoValueEquals(questions[i].value, value)) {
          this.isSettingValue = true;
          questions[i].updateValueFromSurvey(value);
          this.isSettingValue = false;
        }
      }
    }
  }

  protected isValueChanged(name: string, newValue: any): boolean {
    const oldItemData = this.data.getItemData(this);
    const oldValue = !!oldItemData ? oldItemData[name] : undefined;
    return !Helpers.isTwoValueEquals(newValue, oldValue, false, true, false);
  }

  protected getSharedQuestionByName(columnName: string): Question {
    return !!this.data
      ? this.data.getSharedQuestionFromArray(columnName, this.getIndex())
      : null;
  }
}